import { formatNumber, formatPercent } from "../../components/charts/formatters"
import { typeIdTotalsToPlatformBreakdown } from "./pulseCardReport"
import { socialNetworkTypeIdToLabel } from "./socialNetworkCatalog"

export { socialNetworkTypeIdToLabel } from "./socialNetworkCatalog"

/** Format a Date as `YYYY-MM-DD` for inbox-health query params. */
export function formatInboxHealthDateParam(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ""
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Days before today for Soho Pulse `previousDate` query params (e.g. today the 6th → the 4th). */
export const PULSE_PREVIOUS_DATE_OFFSET_DAYS = 2

/**
 * Shared previousDate for inbox-health, daily performance, and daily post stats.
 */
export function getPulsePreviousDate(
  currentDate = new Date(),
  daysBefore = PULSE_PREVIOUS_DATE_OFFSET_DAYS,
) {
  const d = new Date(currentDate)
  d.setDate(d.getDate() - daysBefore)
  return formatInboxHealthDateParam(d)
}

/** Previous comparison date for inbox-health (yesterday). */
export function getInboxHealthPreviousDate(currentDate = new Date()) {
  return getPulsePreviousDate(currentDate, 1)
}

/** Soho Pulse engagement/followers: single-day window (yesterday → today). */
export function getPulseSnapshotDateRange(currentDate = new Date()) {
  const end = new Date(currentDate)
  const start = new Date(currentDate)
  start.setDate(start.getDate() - 1)
  return {
    startDate: formatInboxHealthDateParam(start),
    endDate: formatInboxHealthDateParam(end),
  }
}

const EMPTY_INBOX_HEALTH_STATS = {
  inboundMessageCount: 0,
  inboundMessageDifference: 0,
  messageCount: 0,
  alertCount: 0,
  negativePercentage: 0,
  topSocialNetworkTypeId: null,
}

/** Normalize GET /report/messages/clients/{id}/daily/stats */
export function normalizeInboxHealthStats(payload) {
  const src =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
        ? payload.data
        : payload
      : null
  if (!src) return { ...EMPTY_INBOX_HEALTH_STATS }
  return {
    inboundMessageCount: Number(src.inboundMessageCount) || 0,
    inboundMessageDifference: Number(src.inboundMessageDifference) || 0,
    messageCount: Number(src.messageCount) || 0,
    alertCount: Number(src.alertCount) || 0,
    negativePercentage: Number(src.negativePercentage) || 0,
    topSocialNetworkTypeId: src.topSocialNetworkTypeId ?? null,
  }
}

export function pulseInboxStatsForClient(sliceState, clientKey) {
  if (clientKey == null || clientKey === "") return null
  if (sliceState?.status !== "succeeded") return null
  if (String(sliceState.fetchedClientId ?? "") !== String(clientKey)) return null
  return sliceState.stats ?? null
}

/**
 * Merge daily/stats inbox payload into the dashboard pulse card (keeps goal from base card).
 */
export function buildInboxHealthCard(baseCard, stats, socialNetworkTypes = []) {
  if (!baseCard) return baseCard

  const data = normalizeInboxHealthStats(stats)
  const catalogLabel = socialNetworkTypeIdToLabel(
    socialNetworkTypes,
    data.topSocialNetworkTypeId,
  )
  const topPlatform = catalogLabel || "—"

  const inbound = data.inboundMessageCount
  const difference = data.inboundMessageDifference
  const previous = inbound - difference

  let sparklineTrend = "stable"
  if (difference > 0) sparklineTrend = "up"
  else if (difference < 0) sparklineTrend = "down"

  const platformTotalsByTypeId = new Map()
  if (data.topSocialNetworkTypeId != null && inbound > 0) {
    platformTotalsByTypeId.set(Number(data.topSocialNetworkTypeId), inbound)
  }

  return {
    ...baseCard,
    title: baseCard.title || "Inbox Health",
    subLabel: "Inbound messages",
    value: formatNumber(inbound),
    trophyInsight: `Most conversations came from ${topPlatform}.`,
    platformBreakdown: typeIdTotalsToPlatformBreakdown(platformTotalsByTypeId, socialNetworkTypes),
    sparklineData: [previous, inbound],
    sparklineTrend,
    sparklinePercentChange: null,
    detailRows: [
      { label: "Messages", value: formatNumber(data.messageCount) },
      { label: "Alerts", value: formatNumber(data.alertCount) },
      { label: "Negative %", value: formatPercent(data.negativePercentage, 1) },
    ],
  }
}
